import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";
import { useCamera } from "./useCamera.js";

// Symmetry Checker — live camera with a mirror overlay. The user picks
// which half of the frame to mirror; the tool shows that half doubled
// over its axis, creating a perfectly symmetric image. Comparing this
// to the real subject shows how symmetric the original was.
//
// Implementation: a <video> stream is drawn onto a <canvas> at every
// animation frame, with the chosen half copied/flipped onto the other
// half. We use canvas because pure CSS can't do "left half copied onto
// right half flipped" cleanly.
//
// V57.2: video element uses opacity:0 + pointer-events:none instead of
// display:none, because iOS Safari pauses display:none videos and the
// canvas draw loop then sees readyState < 2 forever (= no display).
// Keeping the video rendered (but invisible to the user) lets it play.

const MIRROR_MODES = [
  { id: "left", label: "Mirror left → right" },
  { id: "right", label: "Mirror right → left" },
  { id: "top", label: "Mirror top → bottom" },
  { id: "bottom", label: "Mirror bottom → top" },
];

export default function SymmetryChecker() {
  const { state: camState, videoRef, enable } = useCamera({ facingMode: "environment" });
  const [mode, setMode] = useState("left");
  const [showSplit, setShowSplit] = useState(true);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Start the draw loop when camera goes "ok".
  useEffect(() => {
    if (camState === "ok") {
      rafRef.current = requestAnimationFrame(draw);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camState]);

  const draw = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || v.readyState < 2) { rafRef.current = requestAnimationFrame(draw); return; }
    const w = v.videoWidth, h = v.videoHeight;
    if (!w || !h) { rafRef.current = requestAnimationFrame(draw); return; }
    if (c.width !== w) c.width = w;
    if (c.height !== h) c.height = h;
    const ctx = c.getContext("2d");
    const m = modeRef.current;

    if (m === "left") {
      // Draw left half normally, then mirror it onto right half.
      ctx.drawImage(v, 0, 0, w / 2, h, 0, 0, w / 2, h);
      ctx.save();
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(v, 0, 0, w / 2, h, 0, 0, w / 2, h);
      ctx.restore();
    } else if (m === "right") {
      ctx.drawImage(v, w / 2, 0, w / 2, h, w / 2, 0, w / 2, h);
      ctx.save();
      ctx.translate(w / 2, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(v, w / 2, 0, w / 2, h, -w / 2, 0, w / 2, h);
      ctx.restore();
    } else if (m === "top") {
      ctx.drawImage(v, 0, 0, w, h / 2, 0, 0, w, h / 2);
      ctx.save();
      ctx.translate(0, h);
      ctx.scale(1, -1);
      ctx.drawImage(v, 0, 0, w, h / 2, 0, 0, w, h / 2);
      ctx.restore();
    } else if (m === "bottom") {
      ctx.drawImage(v, 0, h / 2, w, h / 2, 0, h / 2, w, h / 2);
      ctx.save();
      ctx.translate(0, h / 2);
      ctx.scale(1, -1);
      ctx.drawImage(v, 0, h / 2, w, h / 2, 0, -h / 2, w, h / 2);
      ctx.restore();
    }
    rafRef.current = requestAnimationFrame(draw);
  };

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    // Stream cleanup handled by useCamera hook on unmount
  }, []);

  return (
    <ToolsShell title="Symmetry Checker">
      {camState !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Live camera with a mirror overlay — see how symmetric a drawing, sculpture, or any object is. Useful for art-in-progress, Lego builds, woodworking.
          </p>
          <button onClick={enable} style={primaryBtn}>Enable camera</button>
          <SensorNotice state={camState === "idle" ? "prompt" : camState} what="Camera" />
        </div>
      )}

      {camState === "ok" && (
        <>
          <div style={{ position: "relative", width: "100%", maxWidth: 540, aspectRatio: "3 / 4", background: "#000", borderRadius: 12, overflow: "hidden", margin: "0 auto" }}>
            {/* V57.2: opacity:0 + pointer-events:none instead of display:none.
                iOS Safari pauses display:none videos, which causes the canvas
                draw loop to stall waiting for readyState >= 2 forever. Keeping
                the video rendered (but invisible) lets the browser play it. */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: "none",
                left: 0,
                top: 0,
              }}
            />
            <canvas
              ref={canvasRef}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
            {/* Split line overlay */}
            {showSplit && (
              <div style={{
                position: "absolute",
                ...(mode === "left" || mode === "right"
                  ? { left: "50%", top: 0, bottom: 0, width: 1.5, transform: "translateX(-0.75px)" }
                  : { top: "50%", left: 0, right: 0, height: 1.5, transform: "translateY(-0.75px)" }),
                background: TOOLS_ACCENT,
                opacity: 0.6,
              }} />
            )}
          </div>

          <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
            {MIRROR_MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                style={{
                  background: mode === m.id ? TOOLS_ACCENT : "#1C1E2E",
                  color: mode === m.id ? "#1A0604" : "#9A9DBE",
                  border: "none", borderRadius: 8,
                  padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div style={{ marginTop: 12, textAlign: "center" }}>
            <button
              onClick={() => setShowSplit((s) => !s)}
              style={{ background: "transparent", color: "#9A9DBE", border: "1px solid #262839", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}
            >
              {showSplit ? "Hide" : "Show"} split line
            </button>
          </div>

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center", maxWidth: 380, margin: "14px auto 0" }}>
            The half you select is mirrored over the split — the image you see is what would be perfectly symmetric. Compare with reality to spot asymmetry.
          </p>
        </>
      )}
    </ToolsShell>
  );
}
